import { FLAG_ACTIVE, VEC_X, VEC_Y } from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';

import type { ParticleCore, PoolRange } from 'app/views/home/webgl/particlesOptimised/core/ParticleCore';

/**
 * Integrate kernel — verbatim port of the Inertia EASE path
 * (utils/motion/Inertia.tick line 101):
 *
 *   value += ((target - value) / ease) * window.deltaTime
 *
 * applied to the x / y / alpha / scale lanes in the same order
 * ParticleProperties.tick() ticks its four Inertia instances (the lanes are
 * independent, so order is cosmetic — kept for 1:1 readability against the
 * reference).
 *
 * STAGE 5: the x/y lanes also support Inertia's VECTOR mode
 * (_processVector — ParticleProperties.setVectorStyleMovement, the halftone's
 * drift), gated per axis by the vecFlags lanes. Reference math, verbatim:
 *
 *   d = target - value
 *   if |d| > 10:        force += ±increaseRate * deltaTime
 *   if |d| < reduceAt:  force *= dampen          // per TICK, not per dt — kept
 *   force = clamp(force, ±maxForce)
 *   value += force * deltaTime
 *
 * Vector mode takes the WHOLE Inertia tick (the reference returns before the
 * ease path); alpha/scale never run in vector mode on the home flow
 * (setVectorStyleScaling has no live caller state — not ported).
 *
 * STAGE 6: Inertia.lerpEase on the x/y axes (the spaces squarePaths phase's
 * setEase({...}, true, 60)) — verbatim: while frames remain the ease value
 * steps by `step` once per TICK (not deltaTime-scaled), processed BEFORE the
 * value update and ONLY on the ease path (Inertia.tick returns from vector
 * mode before the lerp block). Arming writes the step/frames lanes
 * (see SpacesDepartment); a plain setEase must zero the frames lanes
 * (Inertia.setEase -> resetEaseLerp).
 *
 * Deliberately NOT ported here:
 *  - Inertia's easeTarget stepping (no live home caller — only the work
 *    tiles use easeTarget);
 *  - alpha/scale lerpEase: unreachable from the home flow (see
 *    ParticleCore's ease-lerp lane notes);
 *  - ParticleProperties' sprite-write thresholds (alpha/scale only written
 *    to the Sprite when the delta exceeds 0.01): that is a retained-sprite
 *    write optimisation, not simulation state — the SoA path uploads the
 *    whole pool buffer regardless, so the thresholds are dropped. The
 *    simulated VALUES match the reference exactly (parity-tested); the
 *    rendered value can differ from the reference's sprite by < 0.01,
 *    which is imperceptible and disappears as both settle.
 */
export function integrateKernel(core: ParticleCore, range: PoolRange, deltaTime: number): void {
	const flags = core.stateFlags;
	const vec = core.vecFlags;

	for (let i = range.start; i < range.end; i++) {
		if (!(flags[i] & FLAG_ACTIVE)) {
			continue;
		}

		if (vec[i] & VEC_X) {
			const d = core.tx[i] - core.x[i];
			if (Math.abs(d) > 10) {
				core.vecForceX[i] += d > 0 ? core.vecIncX[i] * deltaTime : -1 * core.vecIncX[i] * deltaTime;
			}
			if (Math.abs(d) < core.vecReduceAtX[i]) {
				core.vecForceX[i] *= core.vecDampX[i];
			}
			core.vecForceX[i] = Math.max(-1 * core.vecMaxX[i], Math.min(core.vecForceX[i], core.vecMaxX[i]));
			core.x[i] += core.vecForceX[i] * deltaTime;
		} else {
			//Inertia's ease-lerp block — per tick, before the value update:
			if (core.easeLerpFramesX[i] > 0) {
				core.easeX[i] += core.easeLerpStepX[i];
				core.easeLerpFramesX[i]--;
			}
			core.x[i] += ((core.tx[i] - core.x[i]) / core.easeX[i]) * deltaTime;
		}

		if (vec[i] & VEC_Y) {
			const d = core.ty[i] - core.y[i];
			if (Math.abs(d) > 10) {
				core.vecForceY[i] += d > 0 ? core.vecIncY[i] * deltaTime : -1 * core.vecIncY[i] * deltaTime;
			}
			if (Math.abs(d) < core.vecReduceAtY[i]) {
				core.vecForceY[i] *= core.vecDampY[i];
			}
			core.vecForceY[i] = Math.max(-1 * core.vecMaxY[i], Math.min(core.vecForceY[i], core.vecMaxY[i]));
			core.y[i] += core.vecForceY[i] * deltaTime;
		} else {
			if (core.easeLerpFramesY[i] > 0) {
				core.easeY[i] += core.easeLerpStepY[i];
				core.easeLerpFramesY[i]--;
			}
			core.y[i] += ((core.ty[i] - core.y[i]) / core.easeY[i]) * deltaTime;
		}

		core.alpha[i] += ((core.tAlpha[i] - core.alpha[i]) / core.easeAlpha[i]) * deltaTime;
		core.scale[i] += ((core.tScale[i] - core.scale[i]) / core.easeScale[i]) * deltaTime;
	}
}
